import { InteractionType, PopupRequest } from "@azure/msal-browser";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import InvalidTenantPopup from "auth/components/InvalidTenantPopup";
import AuthenticationError from "auth/components/authenticationError";
import AuthenticationLoading from "auth/components/authenticationLoading";
import {
  FORBIDDEN_DESCRIPTION_MODAL,
  UNAUTHENTICATED_DESCRIPTION_MODAL,
} from "auth/constants";
import {
  closeForbiddenError,
  setIsLoggedIn,
} from "auth/store/authenticationSlice";
import { authMode } from "auth/utils/authSettings";
import { getAccessRequest } from "auth/utils/getAccessRequest";
import getQueryParamTenantId from "auth/utils/getQueryParamTenantId";
import { getSignedInAccount } from "auth/utils/getSignedInAccount";
import { scopes } from "auth/utils/scopes";
import ConfirmationModal from "common/components/ConfirmationModal";
import React, { PropsWithChildren, useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";

const MsalAuthenticationWrapper: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const tenantHint = getQueryParamTenantId();
  const dispatch = useAppDispatch();
  const { isUnauthorized, isForbidden } = useAppSelector(
    (state) => state.common.authentication
  );
  const auth = useMsalAuthentication(InteractionType.Redirect, {
    scopes,
    extraQueryParameters: tenantHint ? { tenant_hint: tenantHint } : undefined,
  });
  const { instance } = useMsal();

  const { login, error, result } = auth;

  const isLoading = !result && !error;
  const rpaftenant = result?.account?.idTokenClaims?.rpaf_tenant;

  const handleConfirmSignIn = useCallback(async () => {
    const request = getAccessRequest(instance) ?? { scopes: scopes };

    const extraQueryParameters = tenantHint
      ? { tenant_hint: tenantHint }
      : undefined;

    try {
      const requestWithTenant: PopupRequest = {
        ...request,
        extraQueryParameters,
      };

      await login(InteractionType.Popup, requestWithTenant);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsLoggedIn());
    }
  }, [dispatch, instance, login, tenantHint]);

  const handleConfirmSignOut = useCallback(async () => {
    try {
      const account = getSignedInAccount(instance)!;
      await instance.logoutRedirect({ account });
    } catch (error) {
      console.error(error);
    }
  }, [instance]);

  const handleCancel = useCallback(() => {
    if (isUnauthorized) {
      try {
        const request = getAccessRequest(instance) ?? undefined;
        login(InteractionType.Redirect, request);
      } catch (error) {
        console.error(error);
      }
    } else {
      dispatch(closeForbiddenError());
    }
  }, [dispatch, instance, isUnauthorized, login]);

  const tenantMismatch = tenantHint && rpaftenant && rpaftenant !== tenantHint;

  // if rpaftenant does not match tenantHint, reauthenticate ----------------------
  useEffect(() => {
    if (!isLoading && tenantMismatch)
      login(InteractionType.Redirect, {
        scopes,
        extraQueryParameters: { tenant_hint: tenantHint },
      });
  }, [isLoading, login, tenantHint, tenantMismatch]);
  // ---------------------------------------------------------------------------

  if (error) return <AuthenticationError {...auth} />;
  if (isLoading || tenantMismatch) return <AuthenticationLoading />;

  // B2C returns an empty tenant when it is invalid or when the user lacks permissions to access it.
  if (authMode === "b2c" && !rpaftenant) return <InvalidTenantPopup />;

  return (
    <>
      {children}
      {(isUnauthorized || isForbidden) && (
        <ConfirmationModal
          open={true}
          title={isUnauthorized ? "Authentication Required" : "Forbidden"}
          confirmLabel={isUnauthorized ? "Sign In" : "Sign Out"}
          cancelLabel={isUnauthorized ? undefined : "Cancel"}
          mainButtonType="primary"
          defaultAction="confirm"
          onConfirm={
            isUnauthorized ? handleConfirmSignIn : handleConfirmSignOut
          }
          onCancel={isUnauthorized ? undefined : handleCancel}
        >
          {isUnauthorized
            ? UNAUTHENTICATED_DESCRIPTION_MODAL
            : FORBIDDEN_DESCRIPTION_MODAL}
        </ConfirmationModal>
      )}
    </>
  );
};

export default MsalAuthenticationWrapper;
